// src/App.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db, auth, googleProvider } from './firebase'; 
import { 
  collection, doc, runTransaction, updateDoc, getDoc, setDoc, 
  onSnapshot, query, orderBy, where, getDocs, addDoc, increment, writeBatch 
} from 'firebase/firestore'; 
import { signInWithRedirect, getRedirectResult, onAuthStateChanged, signOut, signInWithCustomToken } from 'firebase/auth';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import liff from '@line/liff';

import toast, { Toaster } from 'react-hot-toast';
import Swal from 'sweetalert2';
import './App.css'; 

import { COLORS } from './theme';
import { 
  formatDateToIso, getMemberName, getMemberRole, getDayOfWeek, 
  formatCurrency, getUserRank, DEFAULT_RANKS,
  hasPriorityRank, isRankAllowed, normalizeRanks 
} from './utils';
import Profile from './Profile';
import Shop from './Shop';
import Shrine from './Shrine';

import AdBanner from './components/AdBanner';
import TeamSection from './components/TeamSection';
import GuideSection from './components/GuideSection';
import RankingSection from './components/RankingSection';
import HomeBody from './components/HomeBody';
import BottomNav from './components/BottomNav'; 
import AdminGuard from './components/AdminGuard'; 
import NewsDetail from './components/NewsDetail';
import Gacha from './components/Gacha';
import Training from './components/Training';
import DeckBuilder from './components/DeckBuilder';
import Arena from './components/Arena';
import Match from './components/Match';
import UserProfileModal from './components/UserProfileModal';
import SponsorsPage from './components/SponsorsPage';
import SponsorApplyForm from './components/SponsorApplyForm';
import ContactRoom from './components/ContactRoom';

// ★ ブラックリスト方式：機密フィールドを絶対にキャッシュ・公開しない
const SECRET_FIELDS = new Set([
    'transferPassword', 'lineId', 'phoneNumber', 'email', 'paypayId',
    'googleUid', 'idCooperation', 'invitedBy', 'banReason', 'banUntil'
]);
const toPublic = (u) => {
    const o = {};
    Object.keys(u).forEach(k => { 
        if (!SECRET_FIELDS.has(k)) o[k] = u[k]; 
    });
    return o;
};

function App() {
  const [activeTab, setActiveTab] = useState('home');
  const [matchData, setMatchData] = useState(null); 
  const [events, setEvents] = useState([]); 
  const [lineUser, setLineUser] = useState(null);
  const [userInfo, setUserInfo] = useState(null); 
  
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [isLiffLoading, setIsLiffLoading] = useState(true); 
  const [openChatId, setOpenChatId] = useState(null);
  // ★ 開いているイベントのチャット（events/{id}/chatMessages）。
  //   以前は event.chat 配列に入っていたが、1MB制限対策でサブコレクションに分離した。
  const [chatInput, setChatInput] = useState('');
    
  const [teamMembers, setTeamMembers] = useState([]); 
  const [allUsers, setAllUsers] = useState([]); 

  const [profileModalUserId, setProfileModalUserId] = useState(null);
  const [activeChatMessages, setActiveChatMessages] = useState([]);

  // ★ チャットを開いている間だけ購読する。
  //   閉じたら解除してリスナーが残らないようにする。
  //   ※ 他の useEffect の内側に置くとフックのルール違反になるので、
  //     必ずコンポーネント直下（トップレベル）に置くこと。
  useEffect(() => {
      if (!openChatId) {
          setActiveChatMessages([]);
          return;
      }
      const q = query(
          collection(db, 'events', openChatId, 'chatMessages'),
          orderBy('createdAt', 'asc')
      );
      const unsub = onSnapshot(q, (snap) => {
          setActiveChatMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }, (err) => {
          console.error('チャット取得エラー:', err?.code || '', err?.message || err);
          setActiveChatMessages([]);
      });
      return () => unsub();
  }, [openChatId]);
  
  const liffInitDone = useRef(false);
  // ★ Firebase Auth の UID を LINE の userId に揃える処理を、1セッション1回だけ試す
  const authUpgradeTried = useRef({});

  const [appConfig, setAppConfig] = useState({ 
      appTitle: 'Revengers FC', 
      seasonStartDate: '', 
      adConfig: null, 
      bankInfo: '',
      adminPayPayId: '',
      faviconUrl: '',
      topBannerUrl: '',
      teamEmblemUrl: '',
      teamPhotoUrl: '',
      leagueName: '',
      operatingCompany: '',
      teamIntro: '',
      establishedDate: '',
      homeGround: '',
      snsLinks: { web: '', x: '', instagram: '', tiktok: '', youtube: '', facebook: '', threads: '', custom: '' },
      showMemberList: true,
      requireApproval: true,
      pointNameLabel: 'RB Coin', 
      pointUnitLabel: 'RB',       
      ranks: DEFAULT_RANKS 
  });
  const [rankingMode, setRankingMode] = useState('participation');
  const [userStatus, setUserStatus] = useState('loading'); 

  useEffect(() => {
    const unsubConfig = onSnapshot(doc(db, 'settings', 'config'), (doc) => {
        if (doc.exists()) {
            const data = doc.data();
            if (!data.pointNameLabel) data.pointNameLabel = 'RB Coin';
            if (!data.pointUnitLabel) data.pointUnitLabel = 'RB';
            if (!data.ranks) data.ranks = DEFAULT_RANKS;
            if (data.requireApproval === undefined) data.requireApproval = true;
            
            setAppConfig(prev => ({...prev, ...data})); 
            if (data.faviconUrl) {
                let link = document.querySelector("link[rel~='icon']");
                if (!link) {
                    link = document.createElement('link');
                    link.rel = 'icon';
                    document.getElementsByTagName('head')[0].appendChild(link);
                }
                link.href = data.faviconUrl;
            }
        }
    });
    return () => unsubConfig();
  }, []);

  useEffect(() => {
      if (!userInfo?.userId) return;
      if (userInfo.status !== 'pending') return;
      if (appConfig.requireApproval !== false) return;

      updateDoc(doc(db, 'users', userInfo.userId), { status: 'approved' })
          .catch(e => console.error("自動承認エラー:", e));
  }, [userInfo?.userId, userInfo?.status, appConfig.requireApproval]);

  useEffect(() => {
      if (userInfo?.status) {
          setUserStatus(userInfo.status);
      }
  }, [userInfo?.status]);

  useEffect(() => {
      const fetchAllUsers = async () => {
          if (allUsers.length > 0) return; 

          // ★ 機密を含む旧キャッシュを明示的に破棄
          try {
              localStorage.removeItem('rfc_users_cache'); 
              localStorage.removeItem('rfc_users_cache_time');
              localStorage.removeItem('rfc_users_cache_v2');
              localStorage.removeItem('rfc_users_cache_time_v2');
          } catch(e) {}

          // ==========================================================
          // 「チーム」タブに出す、リアルのチームメンバー名簿。
          //
          // 判定はランクだけで行う。
          // 管理画面の「会員ランク設定」で一番上にあるランク（正規メンバー）を
          // 持っている人が載る。載せる・外すは管理画面のランクで操作する。
          //
          // ★変更: 以下の2つを判定から外した。
          //   1. isRetired
          //      プロフィールの「休部・引退する（ガチャ排出停止）」チェック。
          //      説明文のとおりカードガチャの排出を止めるための項目で、
          //      リアルのチーム所属とは関係がない。
          //      カードに使われたくないだけの人が名簿から消えていた。
          //   2. 180日ルール（lastDecayDate / lastLogin / createdAt）
          //      アプリを開いていないことと、チームに所属していないことは別。
          //      とくに lastDecayDate は能力値の減衰処理の日付で、
          //      リアルの活動状況を表していない。
          //      これが古いまま止まっている人は、ログインしていても
          //      いずれ名簿から消えていた。
          //
          //   なおガチャ側（index.js の排出対象判定）は従来どおり
          //   isRetired と180日ルールを見る。あちらはカードの話なので変えない。
          // ==========================================================
          const processTeamMembers = (usersList) => {
              const topRankId = (appConfig.ranks && appConfig.ranks.length > 0) ? appConfig.ranks[0].id : null;
              const prioMembers = usersList.filter(u => {
                  if (!u || !topRankId) return false;
                  const rank = getUserRank(u, appConfig.ranks);
                  return rank && rank.id === topRankId; 
              });
              
              prioMembers.sort((a,b) => (Number(a.number)||999) - (Number(b.number)||999));
              
              const displayMembers = prioMembers.map(u => ({
                  ...u,
                  rankIcon: getUserRank(u, appConfig.ranks).icon
              }));
              
              setTeamMembers(displayMembers);
          };

          // ==========================================================
          // メンバー全件（users_public）のキャッシュ
          //
          // ★変更: 保持時間を 5分 → 60分 にした。
          //   ここは users_public を全件読むため、1回の実行で
          //   メンバー数ぶん（600件超）の読み取りが発生する。
          //   アプリを開くたびに走るので、Firestore の読み取り数を
          //   最も消費している箇所になっていた。
          //   名簿・ランキングが数十分遅れて更新されても実害はないため、
          //   保持時間を延ばして読み取り回数を 1/12 に減らす。
          //
          //   ★ 管理画面でランクを変更した直後など、すぐ反映させたいときは
          //     window.clearUsersCache() をコンソールで実行する（下で定義）。
          // ==========================================================
          const cacheKey = 'rfc_users_cache_v3';
          const cacheTimeKey = 'rfc_users_cache_time_v3';
          const CACHE_TTL_MS = 60 * 60 * 1000;   // 60分
          const now = new Date().getTime();
          const cached = localStorage.getItem(cacheKey);
          const cachedTime = localStorage.getItem(cacheTimeKey);

          if (cached && cachedTime && (now - Number(cachedTime) < CACHE_TTL_MS)) {
              try {
                  const parsedUsers = JSON.parse(cached);
                  setAllUsers(parsedUsers);
                  processTeamMembers(parsedUsers);
                  return;
              } catch(e) { console.error("Cache parsing error", e); }
          }

          try {
              const q = query(collection(db, "users_public"));
              const snap = await getDocs(q);
              
              const users = snap.docs.map(d => toPublic(d.data()));
              setAllUsers(users);
              
              try {
                  localStorage.setItem(cacheKey, JSON.stringify(users));
                  localStorage.setItem(cacheTimeKey, now.toString());
              } catch(e) { console.log('Cache save skipped (Quota Exceeded etc.)'); }

              processTeamMembers(users);
          } catch (error) {
              console.error("ユーザー情報の取得に失敗しました", error);
          }
      };
      
      if (userInfo || activeTab === 'ranking' || activeTab === 'team' || activeTab === 'gacha' || activeTab === 'arena' || activeTab === 'deck') {
          fetchAllUsers();
      }
  }, [appConfig.ranks, userInfo?.userId, activeTab]); 

  // ★追加: メンバー一覧のキャッシュを手動で消すための入口。
  //   キャッシュを60分保持するようにしたため、管理画面でランクを変えた直後に
  //   確認したい場合の逃げ道を用意しておく。
  //   ブラウザのコンソールで clearUsersCache() と打つと、消してから再読み込みする。
  useEffect(() => {
      window.clearUsersCache = () => {
          localStorage.removeItem('rfc_users_cache_v3');
          localStorage.removeItem('rfc_users_cache_time_v3');
          console.log('メンバー一覧のキャッシュを削除しました。再読み込みします。');
          window.location.reload();
      };
      return () => { delete window.clearUsersCache; };
  }, []);

  useEffect(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 2); 
    const fetchStartStr = formatDateToIso(d);

    const qRecent = query(
        collection(db, "events"), 
        where("isoDate", ">=", fetchStartStr), 
        orderBy("isoDate", "asc")
    );

    const unsubEvents = onSnapshot(qRecent, (snapshot) => {
        const recentEvents = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const combined = [...recentEvents].sort((a, b) => {
            const dateA = a.isoDate || '';
            const dateB = b.isoDate || '';
            return dateA.localeCompare(dateB);
        });
        setEvents(combined);
        setIsLoading(false);
    }, (error) => {
        console.error("Event fetch error:", error);
        toast.error("データの取得に失敗しました");
        setIsLoading(false);
    });

    // ★ Firebase Auth の UID を LINE の userId に統一する。
    //   これにより Firestore ルールを request.auth.uid == userId で書けるようになる。
    //   失敗しても例外を上に伝播させない（現段階はルールが緩いため、従来どおり動作する）。
    const upgradeAuthUid = async (mode) => {
        // ★ line / google でフラグを分ける。
        //   共通にすると、先にLINE側が実行されたセッションで
        //   Google側が無条件にスキップされてしまう。
        if (authUpgradeTried.current[mode]) return;
        authUpgradeTried.current[mode] = true;
        try {
            const fns = getFunctions(getApp(), 'asia-northeast1');
            let res;
            if (mode === 'line') {
                const accessToken = liff.getAccessToken();
                if (!accessToken) return;
                res = await httpsCallable(fns, 'getLineCustomToken')({ accessToken });
            } else {
                res = await httpsCallable(fns, 'getLinkedCustomToken')();
            }
            if (res?.data?.customToken) {
                await signInWithCustomToken(auth, res.data.customToken);
                console.log(`[auth] UIDをLINE userIdに統一しました (${mode}):`, auth.currentUser?.uid);
            } else {
                console.warn(`[auth] customTokenが返ってきませんでした (${mode})`);
            }
        } catch (e) {
            console.warn(`[auth] UIDの統一をスキップしました (${mode}):`, e?.code || '', e?.message || e);
        }
    };

    const initLiff = async () => {
        if (liffInitDone.current) return; 
        liffInitDone.current = true;
        try {
            await liff.init({ 
                liffId: "2008747486-Wzf2uSvJ",
                withLoginOnExternalBrowser: false 
            }); 

            if (liff.isLoggedIn()) {
                const profile = await liff.getProfile();
                setLineUser(profile);
                await checkUserStatus(profile);
                // ★ すでに Firebase 側も LINE の userId でログイン済みなら、鍵の再発行は不要。
                //   （通常の再訪問時はここを通らないが、エラー復帰パスから入ることがある）
                if (auth.currentUser?.uid !== profile.userId) {
                    // ドキュメントが存在する状態になってから実行する（awaitしない：UIをブロックしない）
                    upgradeAuthUid('line');
                }
            } else {
                setUserStatus('guest');
            }
        } catch (err) { 
            console.error("LIFF Init Error:", err);
            liffInitDone.current = false; 
            setUserStatus('guest');
        } finally {
            setIsLiffLoading(false);
        }
    };

    // ★ signInWithRedirect から戻ってきた際のエラーを拾う。
    //   これが無いと、リダイレクトが失敗しても無反応に見える。
    getRedirectResult(auth).catch((e) => {
        console.error('[auth] リダイレクトログインに失敗:', e?.code || '', e?.message || e);
        toast.error('Googleログインに失敗しました');
    });

    const unsubscribeAuth = onAuthStateChanged(auth, async (gUser) => {
        try {
            if (gUser) {
                // ★ UID がそのまま users のドキュメントID（= LINE userId）の場合
                const selfSnap = await getDoc(doc(db, 'users', gUser.uid));
                if (selfSnap.exists()) {
                    const selfData = selfSnap.data();
                    setLineUser({
                        userId: gUser.uid,
                        displayName: selfData.displayName || gUser.displayName,
                        pictureUrl: selfData.pictureUrl || gUser.photoURL
                    });
                    setUserInfo(selfData);
                    setUserStatus(selfData.status || 'pending');
                    setIsLiffLoading(false);

                    // ★ lastLogin の更新はここで行う。
                    //   checkUserStatus はカスタムトークンでサインインする前に走るため、
                    //   request.auth が null でルールに弾かれる（以前は if true だったので通っていた）。
                    //   また、セッションが残っている再訪問時は
                    //   checkUserStatus 自体が呼ばれないので、ここで拾う必要がある。
                    try {
                        const lastLoginTime = selfData.lastLogin ? new Date(selfData.lastLogin).getTime() : 0;
                        if (Date.now() - lastLoginTime > 60 * 60 * 1000) {
                            const loginUpdates = { lastLogin: new Date().toISOString() };
                            if (gUser.photoURL && gUser.photoURL !== selfData.pictureUrl) {
                                loginUpdates.pictureUrl = gUser.photoURL;
                            }
                            await updateDoc(doc(db, 'users', gUser.uid), loginUpdates);
                        }
                    } catch (e) {
                        console.warn('lastLogin の更新に失敗:', e?.code || '', e?.message || e);
                    }
                    return;
                }

                const uq = query(collection(db, 'users'), where('googleUid', '==', gUser.uid));
                const snap = await getDocs(uq);
                if (!snap.empty) {
                    const linkedDoc = snap.docs[0];
                    const linkedData = linkedDoc.data();
                    
                    setLineUser({
                        userId: linkedDoc.id, 
                        displayName: linkedData.displayName || gUser.displayName,
                        pictureUrl: linkedData.pictureUrl || gUser.photoURL
                    });
                    setUserInfo(linkedData);
                    setUserStatus(linkedData.status || 'pending');
                    setIsLiffLoading(false);
                    // ★ GoogleのUIDを LINE userId に差し替える（再発火して上の分岐に入る）
                    upgradeAuthUid('google');
                } else {
                    // ★ signInWithRedirect にしたことで handleGoogleLogin 内の後続処理が
                    //   実行されなくなったため、未連携の案内はここで行う。
                    Swal.fire({
                        title: '未連携のアカウントです',
                        html: `
                            <div style="text-align:left; font-size:13px; color:#555; line-height:1.6;">
                                このGoogleアカウントはチームアプリと連携されていません。<br><br>
                                <b>【PWA連携手順】</b><br>
                                1. いつも通り<b>LINEから</b>アプリを開く<br>
                                2. 下部メニューの「マイページ（プロフィール）」を開く<br>
                                3. 設定画面の<b>「Googleアカウント連携」</b>ボタンを押す<br><br>
                                連携完了後、再度ホーム画面のアイコンからログインしてください。
                            </div>
                        `,
                        icon: 'warning',
                        confirmButtonText: '閉じる',
                        confirmButtonColor: COLORS.primary
                    });
                    await signOut(auth); 
                }
            } else {
                await initLiff();
            }
        } catch (e) {
            console.error("Auth state error:", e);
            liffInitDone.current = false; 
            await initLiff();
        }
    });

    return () => {
        unsubEvents(); 
        unsubscribeAuth();
    };
  }, []); 

  const handleLineLogin = () => {
      const currentUrl = window.location.href;
      liff.login({ redirectUri: currentUrl });
  };

  const handleGoogleLogin = async () => {
      try {
          // これを実行すると、一瞬でGoogleログイン画面に移動します
          await signInWithRedirect(auth, googleProvider);
      } catch (error) {
          console.error("Google Login Error:", error);
          toast.error("ログイン処理に失敗しました");
      }
  };

  const checkUserStatus = async (profile) => {
      try {
          const userRef = doc(db, 'users', profile.userId);
          const userSnapFirst = await getDoc(userRef);

          if (!userSnapFirst.exists()) {
               const configSnap = await getDoc(doc(db, 'settings', 'config'));
               const requireApproval = configSnap.exists() ? (configSnap.data().requireApproval !== false) : true;
               const initialStatus = requireApproval ? 'pending' : 'approved';

               const safePic = profile.pictureUrl || '';
               let safeName = profile.displayName;
               
               const newData = {
                   userId: profile.userId,
                   displayName: safeName, 
                   pictureUrl: safePic, 
                   status: initialStatus,
                   membership: 'visitor', 
                   rankId: ['visitor'], 
                   role: 'member',
                   points: 0, 
                   totalPaid: 0, 
                   // ★追加: 能力振り分け系の初期値。
                   //   これまで未設定で、Profile.js が「100 + ...」と計算していたため
                   //   表示上だけ100に見えていた。加算方式に切り替えた今は、
                   //   availablePoints を実データとして持たせないと特訓しても増えない。
                   baseStats: { offense: 0, defense: 0, stamina: 0, speed: 0, technique: 0 },
                   stats: { participations: 0, goals: 0, assists: 0, moms: 0 },
                   trainingPoints: 0,
                   matchPoints: 0,
                   availablePoints: 100, // 初期の振り分け可能ポイント
                   lastLogin: new Date().toISOString(),
                   createdAt: new Date().toISOString()
               };

               try {
                   await setDoc(userRef, newData);
                   await setDoc(doc(db, 'users_public', profile.userId), toPublic(newData));
               } catch (writeError) {
                   console.error("名前の保存に失敗しました。安全な名前で再試行します。", writeError);
                   newData.displayName = "Member_" + profile.userId.substring(0, 5); 
                   await setDoc(userRef, newData);
                   await setDoc(doc(db, 'users_public', profile.userId), toPublic(newData));
                   toast("名前に特殊文字が含まれていたため、一時的な名前で登録しました");
               }
               
               setUserInfo(newData);
               setUserStatus(initialStatus);
          } else {
               const data = userSnapFirst.data();
               setUserInfo(data);
               setUserStatus(data.status || 'pending'); 
               
               const lastLogin = data.lastLogin ? new Date(data.lastLogin).getTime() : 0;
               const now = new Date().getTime();
               
               let updates = {};
               if (now - lastLogin > 60 * 60 * 1000) {
                   updates.lastLogin = new Date().toISOString();
               }
               if (profile.pictureUrl && profile.pictureUrl !== data.pictureUrl) {
                   updates.pictureUrl = profile.pictureUrl;
               }

               // ==========================================================
               // 期限切れランクの掃除
               //
               // ★変更点（3つ）
               //  1. 掃除の対象が rankId だけだった。
               //     rankIds / membership は放置されるため、期限が切れても
               //     そちら経由でランクが残り続けていた。
               //     → normalizeRanks() で5項目すべてを整合させる。
               //  2. updates.rankId に「配列」を書いていた。
               //     データ設計上 rankId は文字列1つ。ここが配列型の
               //     ユーザーを生み出す発生源になっていた。
               //     → normalizeRanks() が返す文字列を書く。
               //  3. 日付として壊れている値でもランクを剥奪していた。
               //     → 解釈できない日付は「期限なし」とみなして残す。
               //       （utils.js の getUserRankIds と同じ扱い）
               //
               //  なお期限切れの除外そのものは getUserRankIds() が行うため、
               //  ここでは「rankExpiries から切れた項目を消す」ことと
               //  「その結果をドキュメントへ永続化する」ことだけを行う。
               // ==========================================================
               if (data.rankExpiries && typeof data.rankExpiries === 'object') {
                   let isChanged = false;
                   const newRankExpiries = {};

                   Object.keys(data.rankExpiries).forEach(rankKey => {
                       const expiryDateStr = data.rankExpiries[rankKey];
                       const expiryTime = expiryDateStr ? new Date(expiryDateStr).getTime() : NaN;

                       if (expiryDateStr && Number.isFinite(expiryTime) && now > expiryTime) {
                           // 期限切れ → rankExpiries から削除する
                           isChanged = true;
                       } else {
                           // 有効期限内、または日付として壊れている（＝誤って剥奪しない）
                           newRankExpiries[rankKey] = expiryDateStr;
                       }
                   });

                   if (isChanged) {
                       // normalizeRanks は内部で getUserRankIds を呼ぶため、
                       // 期限切れランクを除外した状態の rankIds / rankId / membership が得られる。
                       const { rankIds, rankId, membership } = normalizeRanks(data);

                       updates.rankIds = rankIds;
                       updates.rankId = rankId;
                       updates.membership = membership;
                       // AdminUsers.toggleUserRank と同じく旧フラグも揃えておく
                       updates.isMonthly = rankIds.includes('monthly');
                       updates.isCollaborator = rankIds.includes('collaborator');
                       updates.rankExpiries = newRankExpiries;
                   }
               }
               
               if (Object.keys(updates).length > 0) {
                   updateDoc(userRef, updates).catch(e=>console.log(e));
               }

               try {
                   const publicData = toPublic({ ...data, ...updates });
                   await setDoc(doc(db, 'users_public', profile.userId), publicData, { merge: true });
               } catch (e) {
                   console.error("サイレント同期エラー:", e);
               }
          }
      } catch (e) {
          console.error("User check error:", e);
          Swal.fire({
              title: 'ログインエラー',
              text: 'システムエラーが発生しました: ' + e.message,
              icon: 'error'
          });
          setUserStatus('error');
      }
  };

  useEffect(() => {
      if (lineUser && lineUser.userId) {
          const unsub = onSnapshot(doc(db, 'users', lineUser.userId), (docSnap) => {
              if (docSnap.exists()) {
                  setUserInfo(docSnap.data());
              }
          });
          return () => unsub();
      }
  }, [lineUser?.userId]);

  const rankingData = useMemo(() => {
      let sorted = [...allUsers];

      if (rankingMode === 'rich') sorted.sort((a,b) => (b.points||0) - (a.points||0));
      else if (rankingMode === 'payer') sorted.sort((a,b) => (b.totalPaid||0) - (a.totalPaid||0));
      else if (rankingMode === 'participation') sorted.sort((a,b) => ((b.stats?.participations)||0) - ((a.stats?.participations)||0));
      else if (rankingMode === 'goals') sorted.sort((a,b) => ((b.stats?.goals)||0) - ((a.stats?.goals)||0));
      else if (rankingMode === 'assists') sorted.sort((a,b) => ((b.stats?.assists)||0) - ((a.stats?.assists)||0));
      else if (rankingMode === 'mom') sorted.sort((a,b) => ((b.stats?.moms)||0) - ((a.stats?.moms)||0));

      return sorted.slice(0, 20).map(u => {
          let countStr = '';
          let rawValue = 0;
          if (rankingMode === 'rich') { countStr = `${(u.points||0).toLocaleString()} ${appConfig.pointUnitLabel}`; rawValue = u.points||0; }
          else if (rankingMode === 'payer') { countStr = `¥${formatCurrency(u.totalPaid||0)}`; rawValue = u.totalPaid||0; }
          else if (rankingMode === 'participation') { countStr = `${u.stats?.participations||0} 回`; rawValue = u.stats?.participations||0; }
          else if (rankingMode === 'goals') { countStr = `${u.stats?.goals||0} G`; rawValue = u.stats?.goals||0; }
          else if (rankingMode === 'assists') { countStr = `${u.stats?.assists||0} A`; rawValue = u.stats?.assists||0; }
          else if (rankingMode === 'mom') { countStr = `${u.stats?.moms||0} 回`; rawValue = u.stats?.moms||0; }

          return { name: u.displayName, count: countStr, pic: u.pictureUrl, rawValue };
      }).filter(item => item.rawValue > 0); 
  }, [allUsers, rankingMode, appConfig.pointUnitLabel]);

  const handleCashPayment = async (event) => {
    if (!lineUser || !userInfo) {
        toast.error("ユーザー情報の読み込み中です。少々お待ちください");
        return;
    }
    
    const now = new Date();
    let cashOpenTime;
    try {
        const [year, month, day] = event.isoDate.split('-').map(Number);
        const [startTimeStr] = event.time.split('-'); 
        const [startHour, startMin] = startTimeStr.split(':').map(Number);
        
        const eventStartTime = new Date(year, month - 1, day, startHour, startMin);
        if (isNaN(eventStartTime.getTime())) throw new Error("Invalid Date");
        cashOpenTime = new Date(eventStartTime.getTime() - 30 * 60 * 1000);
    } catch (e) {
        cashOpenTime = new Date(now.getTime() + 24 * 60 * 60 * 1000); 
    }

    if (now < cashOpenTime) {
        const timeStr = cashOpenTime.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
        Swal.fire({
            title: '受付時間外です',
            html: `現金払いの申請は、当日の<br><b style="color:#d32f2f; font-size:18px;">${timeStr}</b> から可能です。<br><br><small>※現地で管理者に直接現金を渡してから申請してください</small>`,
            icon: 'warning',
            confirmButtonColor: '#ff9800'
        });
        return; 
    }
    
    const userName = userInfo.displayName || lineUser.displayName;
    
    const result = await Swal.fire({ 
        title: '当日現金払い', 
        html: '管理者に直接現金を渡しましたか？<br><br><small>「はい」を押すと管理者に承認リクエストを送ります。</small>', 
        icon: 'question', 
        showCancelButton: true, 
        confirmButtonText: 'はい、申請します',
        confirmButtonColor: COLORS.success,
        cancelButtonText: 'まだです'
    });
    
    if (result.isConfirmed) {
        try {
            await addDoc(collection(db, "payment_requests"), {
                userId: userInfo.userId,
                userName: userName,
                // paypayId は private へ（親には作らない）
                eventId: event.id,
                eventTitle: event.title,
                eventDate: event.isoDate, 
                eventTime: event.time,
                eventVenue: event.venue,
                amount: Number(event.fee) || 0,
                status: 'pending',
                method: 'cash',
                createdAt: new Date().toISOString()
            });
            toast.success("申請しました！承認をお待ちください");
        } catch(e) {
            console.error(e);
            toast.error("申請に失敗しました");
        }
    }
  };

  const handlePointPayment = async (event) => {
      if (!lineUser || !userInfo) return;
      const userName = userInfo.displayName || lineUser.displayName;
      
      const rawFee = event.feePoint !== undefined && event.feePoint !== '' ? event.feePoint : event.fee;
      const fee = Number(rawFee);
      if (!Number.isFinite(fee) || fee < 0) {
          toast.error('参加費が正しく設定されていません。管理者にご連絡ください');
          return;
      }

      const currentPoints = userInfo.points || 0;
      const unit = appConfig.pointUnitLabel || 'RB';

      if (currentPoints < fee) {
          toast.error(`${unit}が足りません (残: ${currentPoints}${unit})`);
          return;
      }

      const result = await Swal.fire({
          title: 'コイン決済',
          html: `<b>${event.title}</b> の参加費 <b>${fee}${unit}</b> を<br>コインで支払いますか？<br><br>現在の残高: <b>${currentPoints} ${unit}</b><br>支払い後: <b style="color:red">${currentPoints - fee} ${unit}</b>`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: '支払う',
          confirmButtonColor: '#e65100',
          cancelButtonText: 'やめる'
      });

      if (!result.isConfirmed) return;

      // ★ 実際の決済は Cloud Functions に任せる。
      //   金額はクライアントから送らない（送ると小額で呼ばれて踏み倒せる）。
      //   サーバーがイベントの参加費とランクから再計算する。
      try {
          const fns = getFunctions(getApp(), 'asia-northeast1');
          const call = httpsCallable(fns, 'payEventFeeWithPoint');
          const res = await call({ eventId: event.id });
          const chargedFee = res?.data?.fee;
          toast.success(`${unit}で支払いました！${Number.isFinite(chargedFee) ? ` (-${chargedFee}${unit})` : ''}`);
      } catch (e) {
          console.error("コイン決済エラー:", e?.code || '', e?.message || e);
          toast.error(e?.message || "支払いに失敗しました");
      }
  };

  const handlePayPayAction = async (event) => {
      if (!lineUser) return;
      // ★ 以前は無言で return していたため、管理画面でPayPay送金URLを
      //   入れ忘れていると「ボタンが効かない」としか見えなかった。
      if (!event.paypayLink) {
          toast.error('このイベントにPayPay送金URLが設定されていません。管理者にご連絡ください');
          return;
      }
      const userName = userInfo?.displayName || lineUser.displayName;
      // ★ 第6段：PayPay ID は private から取得する（親からは削除されるため）
      let userPayPayId = '';
      try {
          const privSnap = await getDoc(doc(db, "users", userInfo.userId, "private", "profile"));
          if (privSnap.exists()) userPayPayId = privSnap.data().paypayId || '';
      } catch (e) {
          console.warn('PayPay ID の取得に失敗:', e?.message || e);
      }
      const adminId = appConfig.adminPayPayId || ''; 
      const amountToPay = Number(event.fee) || 0; 

      let adminIdHtml = '';
      if(adminId) {
          adminIdHtml = `
            <div style="background:#f9f9f9; padding:10px; border-radius:8px; margin-bottom:10px; border:1px solid #ddd;">
               <div style="font-size:10px; color:#666;">送金先 (管理者ID)</div>
               <div style="font-weight:bold; font-size:16px; margin-bottom:5px;">${adminId}</div>
               <button type="button" id="copy-admin-id" style="background:#333; color:white; border:none; border-radius:4px; padding:4px 8px; font-size:12px; cursor:pointer;">IDをコピー</button>
            </div>
          `;
      }

      const result = await Swal.fire({
          title: 'PayPay送金',
          html: `
            <div style="text-align:left; font-size:14px;">
              ${adminIdHtml}
              <p>1. 下のボタンを押してPayPayを起動し、<b>「${amountToPay}円」</b>を送金してください。</p>
              <p>2. <span style="color:#e65100; font-weight:bold;">送金完了後、必ずあなたのPayPay IDを入力して</span>申請ボタンを押してください。</p>
              <p style="margin-top:10px; font-weight:bold; color:#e65100;">あなたのPayPay ID (必須)</p>
            </div>
          `,
          input: 'text',
          inputValue: userPayPayId,
          inputPlaceholder: '例: my_paypay_id',
          inputValidator: (value) => {
              if (!value) {
                  return '入金確認のため、PayPay IDの入力は必須です';
              }
          },
          footer: '<small>※IDを入力しないと申請できません</small>',
          icon: 'info',
          showCancelButton: true,
          confirmButtonText: 'PayPayを起動',
          confirmButtonColor: '#FF0033',
          cancelButtonText: '閉じる',
          didOpen: () => {
              const btn = document.getElementById('copy-admin-id');
              if(btn) {
                  btn.addEventListener('click', async () => {
                      try {
                          await navigator.clipboard.writeText(adminId);
                          btn.textContent = 'コピーしました！';
                          setTimeout(() => btn.textContent = 'IDをコピー', 2000);
                      } catch (e) {
                          btn.textContent = 'コピー失敗';
                      }
                  });
              }
              const b = Swal.getHtmlContainer()?.querySelector('b');
              if (b) b.style.color = '#FF0033';
          }
      });

      if (result.isConfirmed) {
          const inputId = result.value; 
          
          window.open(event.paypayLink, '_blank');
          
          setTimeout(async () => {
              const confirmResult = await Swal.fire({
                  title: '送金はお済みですか？',
                  text: `ID: ${inputId} で申請します。`,
                  icon: 'question',
                  showCancelButton: true,
                  confirmButtonText: 'はい、申請します',
                  confirmButtonColor: COLORS.success,
                  cancelButtonText: 'まだです'
              });

              if (confirmResult.isConfirmed) {
                  try {
                      await addDoc(collection(db, "payment_requests"), {
                          userId: userInfo.userId,
                          userName: userName,
                          paypayId: inputId,
                          eventId: event.id,
                          eventTitle: event.title,
                          eventDate: event.isoDate, 
                          eventTime: event.time,
                          eventVenue: event.venue,
                          amount: amountToPay,
                          status: 'pending',
                          method: 'paypay', 
                          createdAt: new Date().toISOString()
                      });
                      
                      if (inputId && userPayPayId !== inputId) {
                          // ★ 第6段：親には書かず、private のみを更新する
                          await setDoc(
                              doc(db, "users", userInfo.userId, "private", "profile"),
                              { paypayId: inputId, updatedAt: new Date().toISOString() },
                              { merge: true }
                          );
                      }
                      Swal.fire('申請完了', '管理者に通知しました。承認されると「支払済」になります。', 'success');
                  } catch(e) {
                      console.error(e);
                      toast.error("申請に失敗しました");
                  }
              }
          }, 1000);
      }
  };

  const handleShareResult = async (event) => {
      const myTeamName = appConfig.appTitle || '自チーム';
      const opponent = event.opponentName || '相手';
      const score = event.homeScore !== undefined ? `${event.homeScore} - ${event.awayScore}` : 'vs';
      const momText = event.mom ? `\n🏅 MOM: ${event.mom}` : '';
      const text = `🏆 試合結果\n${myTeamName} ${score} ${opponent}${momText}\n\n#${myTeamName.replace(/\s+/g,'')}`;
      if (navigator.share) {
          try { await navigator.share({ title: '試合結果', text: text, url: window.location.href }); } 
          catch (error) { console.log('Error sharing', error); }
      } else {
          try {
              await navigator.clipboard.writeText(text + "\n" + window.location.href);
              toast.success("結果をコピーしました！");
          } catch (e) {
              toast.error("コピーに失敗しました");
          }
      }
  };

  const handleCheckIn = async (event) => {
      if (!lineUser || !userInfo) return; 

      const userName = userInfo.displayName || lineUser.displayName;

      const userRank = getUserRank(userInfo, appConfig.ranks || DEFAULT_RANKS);
      const isFeeFree = userRank.type === 'free';
      const isPaid = event.paidMembers && event.paidMembers.includes(userName);

      if (!isFeeFree && !isPaid) {
          Swal.fire({
              title: 'お支払いはお済みですか？',
              html: 'チーム分け（チェックイン）の前に、<br><b>参加費のお支払い</b>をお願いいたします。',
              icon: 'warning',
              confirmButtonText: '支払い画面へ',
              confirmButtonColor: '#e65100'
          });
          return; 
      }

      let checkInOpenTime;
      try {
          const now = new Date();
          const [year, month, day] = event.isoDate.split('-').map(Number);
          const [startTimeStr] = event.time.split('-'); 
          const [startHour, startMin] = startTimeStr.split(':').map(Number);
          
          const eventStartTime = new Date(year, month - 1, day, startHour, startMin);
          if (isNaN(eventStartTime.getTime())) throw new Error("Invalid Date");
          checkInOpenTime = new Date(eventStartTime.getTime() - 60 * 60 * 1000);
          
          if (now < checkInOpenTime) {
              const timeStr = checkInOpenTime.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
              toast.error(`まだチェックインできません。\n受付開始: ${timeStr}〜`);
              return; 
          }
      } catch (e) {
          console.error("Time check error:", e);
      }

      const checkInType = event.checkInType || 'team'; 
      const eventRef = doc(db, "events", event.id);
      
      try {
          await runTransaction(db, async (transaction) => {
              const sfDoc = await transaction.get(eventRef);
              if (!sfDoc.exists()) throw "Error";
              const data = sfDoc.data();
              const members = data.members || [];
              const myIndex = members.findIndex(m => (m.userId === lineUser.userId) || getMemberName(m) === userName);
              
              if (myIndex === -1) throw "NotJoined";
              const me = members[myIndex];
              if (me.checkedInAt) return; 
              let updatedMe = { ...me, checkedInAt: new Date().toISOString() };
                
              if (checkInType === 'team') {
                  const myRole = getMemberRole(me);
                  const checkedInSameRole = members.filter(m => m.checkedInAt && m.team && getMemberRole(m) === myRole);
                  
                  const whiteCount = checkedInSameRole.filter(m => m.team === 'white').length;
                  const blackCount = checkedInSameRole.filter(m => m.team === 'black').length;
                  const nextTeam = whiteCount <= blackCount ? 'white' : 'black';

                  const checkedInTeamSameRoleMembers = members.filter(m => m.checkedInAt && m.team === nextTeam && getMemberRole(m) === myRole);
                  const maxNum = checkedInTeamSameRoleMembers.reduce((max, m) => Math.max(max, m.number || 0), 0);
                  const nextNumber = maxNum + 1;

                  updatedMe.team = nextTeam;
                  updatedMe.number = nextNumber;
              }
              const newMembers = [...members];
              newMembers[myIndex] = updatedMe;
              
              let updatedPaidMembers = data.paidMembers || [];
              if (isFeeFree && !updatedPaidMembers.includes(userName)) {
                  updatedPaidMembers.push(userName);
              }

              transaction.update(eventRef, { members: newMembers, paidMembers: updatedPaidMembers });
          });
          toast.success("チェックイン完了！");
      } catch (e) {
          if (e === "NotJoined") toast.error("参加していません");
          else console.error(e);
      }
  };

  const handleCancelSelfCheckIn = async (event) => {
      if (!lineUser || !userInfo) return;
      const userName = userInfo.displayName || lineUser.displayName;
      
      const result = await Swal.fire({ 
          title: 'チェックイン取消', text: 'チェックインを取り消しますか？', icon: 'warning', showCancelButton: true, confirmButtonText: 'はい', confirmButtonColor: '#d33'
      });
      if (!result.isConfirmed) return;

      const eventRef = doc(db, "events", event.id);
      try {
          await runTransaction(db, async (transaction) => {
              const sfDoc = await transaction.get(eventRef);
              if (!sfDoc.exists()) throw "Error";
              const data = sfDoc.data();
              const members = data.members || [];
              const myIndex = members.findIndex(m => (m.userId === lineUser.userId) || getMemberName(m) === userName);
              if (myIndex === -1) throw "NotJoined";

              const newMember = { ...members[myIndex] };
              delete newMember.checkedInAt; delete newMember.team; delete newMember.number;
              const newMembers = [...members];
              newMembers[myIndex] = newMember;
              transaction.update(eventRef, { members: newMembers });
          });
          toast.success("チェックインを取り消しました");
      } catch (e) { console.error(e); toast.error("エラーが発生しました"); }
  };

  const handlePostChat = async (event) => {
      if (!lineUser || !chatInput.trim()) return;
      const userName = userInfo?.displayName || lineUser.displayName;
      const myId = userInfo?.userId || lineUser?.userId;

      // ★ チャットは events/{id}/chatMessages サブコレクションに保存する。
      //   以前は event.chat 配列に arrayUnion していたため、
      //   ・1ドキュメント1MB制限にいずれ当たる
      //   ・1件投稿するたびにイベント全体が全員に再配信される
      //   ・ドキュメントIDが無いので削除ボタンを出せない
      //   という問題があった。
      const userPic = lineUser.pictureUrl || '';
      try {
          // ★ メッセージの保存と件数の更新を writeBatch で一気に送る。
          //   別々に await すると、前者が成功して後者が失敗したときに
          //   「保存されているのに送信失敗と表示される」という
          //   一番わかりにくい状態になる。
          //   （通信断だけでなく、chatCount がルールで拒否された場合も同様）
          const batch = writeBatch(db);
          const newMsgRef = doc(collection(db, "events", event.id, "chatMessages"));
          batch.set(newMsgRef, {
              name: userName,
              userId: myId || null,     // 削除権限の判定に使う
              pic: userPic,
              text: chatInput.trim().slice(0, 1000),
              createdAt: new Date().toISOString()
          });
          batch.update(doc(db, "events", event.id), { chatCount: increment(1) });
          await batch.commit();

          setChatInput('');
          toast.success("送信しました");
      } catch (e) {
          console.error('チャット投稿エラー:', e?.code || '', e?.message || e);
          toast.error("送信に失敗しました");
      }
  };

  // ★ 削除は配列の index ではなくドキュメントIDで行う。
  //   index 指定だと、他の人が同時に投稿した場合に
  //   別のメッセージが消える。
  const handleDeleteChat = async (event, messageId) => {
      if (!messageId) return;
      const result = await Swal.fire({ title:'メッセージ削除', text:'このメッセージを削除しますか？', icon:'warning', showCancelButton:true, confirmButtonText:'削除', confirmButtonColor:'#d33' });
      if (!result.isConfirmed) return;

      try {
          // ★ 削除と件数の減算も同じバッチで送る。
          //   片方だけ成功すると、メッセージは消えたのに
          //   件数が減らないというズレが残る。
          const batch = writeBatch(db);
          batch.delete(doc(db, "events", event.id, "chatMessages", messageId));
          batch.update(doc(db, "events", event.id), { chatCount: increment(-1) });
          await batch.commit();

          toast.success("削除しました");
      } catch (e) {
          console.error('チャット削除エラー:', e?.code || '', e?.message || e);
          toast.error("削除に失敗しました");
      }
  };

  const handleEventAction = async (event, role = 'FP') => {
      if (!lineUser) { handleLineLogin(); return; }
      if (event.status === "cancelled") return;
      
      const userName = userInfo?.displayName || lineUser.displayName;
      const userPic = lineUser.pictureUrl || ''; 
      const userId = lineUser.userId;
      const eventRef = doc(db, "events", event.id);

      try {
        const eventSnap = await getDoc(eventRef);
        if (!eventSnap.exists()) { toast.error("イベントが見つかりません"); return; }
        const data = eventSnap.data();
        const members = data.members || [];
        const waitlist = data.waitlist || [];
        
        const isJoined = members.some(m => (m.userId === userId) || getMemberName(m) === userName);
        const isWaiting = waitlist.some(m => (m.userId === userId) || getMemberName(m) === userName);

        let actionType = '';
        let cancelReason = '';
        let inputPass = '';

        if (isJoined || isWaiting) {
            const { value: reason, isDismissed } = await Swal.fire({ title: 'キャンセルしますか？', input: 'text', inputPlaceholder: '理由（任意）', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: '取り消す' });
            if (isDismissed) return;
            actionType = 'cancel';
            cancelReason = reason;
        } else {
            if (!userInfo) {
               toast.error("ユーザー情報の読み込み中です。少々お待ちください");
               return;
            }

            // ★変更: ここで独自に myRankIds を組み立てるのをやめ、
            //   utils.js の getUserRankIds() 系ヘルパーに一本化した。
            //   旧実装は userInfo.rankIds を一切見ておらず（rankId のみ）、
            //   membership も monthly / collaborator としか比較していなかったため、
            //   カスタムランク（rank_xxxxx）保有者の優先権を取りこぼしていた。
            //   EventCard.js 側と判定がズレる原因にもなっていた。

            // ★変更: allowedRanks の判定を isRankAllowed() に集約。
            //   併せて参照元を event → data（取得直後の最新ドキュメント）に修正。
            if (!isRankAllowed(userInfo, data.allowedRanks)) {
                Swal.fire({ title: '参加対象外', text: 'このイベントはあなたの会員ランクでは参加できません。', icon: 'error' });
                return;
            }

            // ★変更: priority の判定を hasPriorityRank() に集約。
            const hasPriority = hasPriorityRank(userInfo, appConfig.ranks || DEFAULT_RANKS);

            const isVisitorRestricted = !hasPriority && data.visitorOpenDate && new Date() < new Date(data.visitorOpenDate);
            if (isVisitorRestricted) {
                Swal.fire({ 
                    title: '先行受付期間中', 
                    html: `現在は優先権のあるメンバーのみ受付中です。<br>一般受付開始までお待ちください。<br><br><small>開始: ${new Date(data.visitorOpenDate).toLocaleString()}</small>`, 
                    icon: 'info' 
                });
                return;
            }

            const limitFP = data.limitFP !== undefined ? data.limitFP : 20;
            const limitGK = data.limitGK !== undefined ? data.limitGK : 2;
            
            const currentFP = members.filter(m => getMemberRole(m) === 'FP').length;
            const currentGK = members.filter(m => getMemberRole(m) === 'GK').length;

            if (data.userPassword) {
                const { value: pass, isDismissed } = await Swal.fire({ title: 'パスワード', input: 'text', showCancelButton: true });
                if (isDismissed) return;
                inputPass = pass;
            }

            let isFull = (role === 'FP' && currentFP >= limitFP) || (role === 'GK' && currentGK >= limitGK);
            if (isFull) {
                const result = await Swal.fire({ title: 'キャンセル待ち登録', text: '定員を超えています。待ち列に並びますか？', icon: 'warning', showCancelButton: true, confirmButtonText: '並ぶ' });
                if (!result.isConfirmed) return;
                actionType = 'wait';
            } else {
                const result = await Swal.fire({ title: '参加確認', text: `${role}として参加しますか？`, icon: 'question', showCancelButton: true, confirmButtonText: '参加する！', confirmButtonColor: '#00c853' });
                if (!result.isConfirmed) return;
                actionType = 'join';
            }
        }

        await runTransaction(db, async (transaction) => {
            const sfDoc = await transaction.get(eventRef);
            if (!sfDoc.exists()) throw "Document does not exist!";
            const currentData = sfDoc.data();
              
            if (actionType === 'join' && currentData.userPassword && currentData.userPassword !== inputPass) {
                throw "wrong_password";
            }

            if (actionType === 'cancel') {
               const currentMembers = currentData.members || [];
               const currentWaitlist = currentData.waitlist || [];
               const leavingMember = currentMembers.find(m => (m.userId === userId) || getMemberName(m) === userName);
               const leavingWaiter = currentWaitlist.find(m => (m.userId === userId) || getMemberName(m) === userName);

               if (leavingMember) {
                   const leavingRole = getMemberRole(leavingMember);
                   let newMembers = currentMembers.filter(m => {
                      if(m.userId && m.userId === userId) return false;
                      if(!m.userId && getMemberName(m) === userName) return false;
                      return true;
                   });
                   let newWaitlist = [...currentWaitlist];
                   const promotedIndex = newWaitlist.findIndex(m => getMemberRole(m) === leavingRole);
                   if (promotedIndex !== -1) { 
                       newMembers.push(newWaitlist[promotedIndex]); 
                       newWaitlist.splice(promotedIndex, 1); 
                   }
                   const cancelLog = { name: userName, reason: cancelReason || "理由なし", canceledAt: new Date().toISOString(), wasWaiting: false };
                   transaction.update(eventRef, { current: newMembers.length, members: newMembers, waitlist: newWaitlist, paidMembers: (currentData.paidMembers || []).filter(name => name !== userName), cancelLogs: [...(currentData.cancelLogs || []), cancelLog] });

               } else if (leavingWaiter) {
                   const newWaitlist = currentWaitlist.filter(m => {
                      if(m.userId && m.userId === userId) return false;
                      if(!m.userId && getMemberName(m) === userName) return false;
                      return true;
                   });
                   const cancelLog = { name: userName, reason: cancelReason || "理由なし", canceledAt: new Date().toISOString(), wasWaiting: true };
                   transaction.update(eventRef, { waitlist: newWaitlist, cancelLogs: [...(currentData.cancelLogs || []), cancelLog] });
               }
            } else if (actionType === 'join' || actionType === 'wait') {
               const newMemberObj = { 
                   userId: userId,
                   name: userName, 
                   role: role, 
                   pictureUrl: userPic, 
                   joinedAt: new Date().toISOString(),
                   profileNumber: userInfo?.number || '', 
                   profilePosition: userInfo?.mainPosition || role
               };
               
               if (actionType === 'join') {
                   transaction.update(eventRef, { 
                       current: (currentData.members || []).length + 1, 
                       members: [...(currentData.members || []), newMemberObj]
                   });
               } else {
                   transaction.update(eventRef, { waitlist: [...(currentData.waitlist || []), newMemberObj] });
               }
            }
        });

        if(actionType === 'cancel') toast.success('キャンセルしました');
        if(actionType === 'wait') toast('キャンセル待ちに登録しました');
        if(actionType === 'join') toast.success(`${role}で参加しました！`);

      } catch (e) {
          if(e === "wrong_password") toast.error("パスワードが違います");
          else {
              console.error(e); 
              const msg = typeof e === 'string' ? e : e.message;
              toast.error("処理に失敗しました: " + (msg || '不明なエラー'));
          }
      }
  };

  const getTileContent = ({ date, view }) => {
    if (view !== 'month') return null;
    const dateString = formatDateToIso(date);
    
    const targetEvents = events.filter(e => e.isoDate === dateString);
    if (targetEvents.length > 0) {
        if (targetEvents.every(e => e.status === 'cancelled')) {
            return <div className="calendar-dot" style={{backgroundColor: '#d32f2f'}}></div>;
        }

        const isJoinedAny = lineUser && targetEvents.some(targetEvent => 
            targetEvent.members && targetEvent.members.some(m => (m.userId === lineUser.userId) || getMemberName(m) === lineUser.displayName)
        );
        
        if (isJoinedAny) {
            return <div className="calendar-icon">⚽</div>;
        } else {
            return <div className="calendar-dot" style={{backgroundColor: COLORS.primary}}></div>;
        }
    }
    return null;
  };
   
  const selectedIsoDate = formatDateToIso(selectedDate);
  
  const displayEvents = events
    .filter(e => e.isoDate === selectedIsoDate)
    .sort((a, b) => {
        const timeA = a.time ? a.time.split('-')[0] : '24:00'; 
        const timeB = b.time ? b.time.split('-')[0] : '24:00'; 
        return timeA.localeCompare(timeB); 
    });

  if (isLiffLoading || userStatus === 'loading') {
    return (
      <div className="container" style={{display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100vh'}}>
         <div className="spinner"></div>
         <p style={{marginTop:'20px', color:'#666'}}>Loading...<br/>※初回は少し時間がかかります</p>
      </div>
    );
  }

  if (userStatus === 'guest') {
      return (
        <div className="container" style={{display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', height:'80vh'}}>
            <h1 style={{color: COLORS.primary, marginBottom:'20px', fontSize:'32px'}}>{appConfig.appTitle}</h1>
            <p className="card" style={{padding:'30px', fontSize:'14px'}}>
                チームメンバー専用アプリです。<br/>
                利用するにはLINEログインが必要です。
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', alignItems: 'center' }}>
                <button onClick={handleLineLogin} className="btn btn-success" style={{padding:'15px 40px', fontSize:'18px', fontWeight:'bold', boxShadow:'0 4px 15px rgba(0,200,83,0.3)', width: '260px'}}>
                    LINEでログイン
                </button>
                
                <button onClick={handleGoogleLogin} className="btn" style={{padding:'10px 20px', fontSize:'14px', fontWeight:'bold', backgroundColor: 'white', color: '#444', border: '1px solid #ccc', boxShadow:'0 2px 5px rgba(0,0,0,0.1)', width: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px'}}>
                    <img src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg" alt="Google" style={{width:'18px', height:'18px'}} />
                    Googleでログイン (PWA用)
                </button>
            </div>
        </div>
      );
  }

  if (activeTab === 'admin') {
      return <AdminGuard userInfo={userInfo} onBack={() => setActiveTab('home')} />;
  }

  if (userStatus === 'pending') {
      return (
        <div className="container" style={{display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center'}}>
            <div style={{fontSize:'60px', marginBottom:'20px'}}>⏳</div>
            <h2 style={{color:'#333'}}>承認待ちです</h2>
            <p className="card">管理者の承認後に利用可能になります。<br/>しばらくお待ちください。</p>
            {lineUser && (<div style={{marginTop:'20px', display:'flex', alignItems:'center', gap:'10px', background:'white', padding:'10px 20px', borderRadius:'30px', boxShadow:'0 2px 5px rgba(0,0,0,0.1)'}}>{lineUser.pictureUrl && <img src={lineUser.pictureUrl} style={{width:'30px', height:'30px', borderRadius:'50%'}} alt="" />}<span style={{fontWeight:'bold'}}>{lineUser.displayName}</span></div>)}
            <div style={{marginTop:'40px'}}><button onClick={()=>setActiveTab('admin')} style={{border:'none', background:'none', color:'#ccc', fontSize:'12px'}}>管理者の方はこちら</button></div>
        </div>
      );
  }

  if (userStatus === 'banned') {
      const banUntilDate = userInfo?.banUntil 
        ? new Date(userInfo.banUntil).toLocaleString('ja-JP', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit' 
          }) 
        : '無期限';

      return (
        <div className="container" style={{textAlign:'center', marginTop:'80px', padding:'20px'}}>
            <div style={{fontSize:'80px', marginBottom:'20px'}}>🚫</div>
            <h2 style={{color: '#d32f2f', fontWeight:'bold'}}>出場停止中</h2>
            
            <div className="card" style={{marginTop:'30px', textAlign:'left', border:`2px solid #d32f2f`, padding:'20px', background:'#fff5f5'}}>
                <p style={{fontWeight:'bold', color:'#333', marginBottom:'8px'}}>🚫 出場停止の理由:</p>
                <p style={{fontSize:'15px', color:'#d32f2f', marginBottom:'20px', lineHeight:'1.5', fontWeight:'bold'}}>
                    {userInfo?.banReason || '規約違反または無断欠席等の迷惑行為のため'}
                </p>
                
                <p style={{fontWeight:'bold', color:'#333', marginBottom:'8px'}}>⏳ 出場停止解除の予定:</p>
                <p style={{fontSize:'18px', color:'#333', fontWeight:'bold'}}>
                    {banUntilDate} まで
                </p>
            </div>

            <p style={{marginTop:'40px', fontSize:'13px', color:'#666', lineHeight:'1.8'}}>
                上記の期限を過ぎると、自動的に制限が解除され、<br/>
                通常通りアプリをご利用いただけるようになります。<br/>
                <span style={{color:'#d32f2f', fontWeight:'bold'}}>※解除までご参加はお控えください。</span>
            </p>
            
            <div style={{marginTop:'50px'}}>
                <button onClick={async () => { await signOut(auth); localStorage.clear(); window.location.reload(); }} style={{background:'none', border:'none', color:'#999', fontSize:'11px', textDecoration:'underline'}}>ログアウトして戻る</button>
            </div>
        </div>
      );
  }

  if (userStatus !== 'approved' || !userInfo) {
      return (
          <div className="container" style={{textAlign:'center', marginTop:'80px', padding:'20px'}}>
              <div style={{fontSize:'60px', marginBottom:'20px'}}>⚠️</div>
              <h2 style={{color:'#333'}}>読み込みエラー</h2>
              <p className="card" style={{fontSize:'13px'}}>
                  アカウント情報を取得できませんでした。<br/>通信環境をご確認のうえ、再読み込みしてください。
              </p>
              <button onClick={() => window.location.reload()} className="btn btn-primary" style={{marginTop:'20px'}}>
                  再読み込み
              </button>
          </div>
      );
  }

  // タブレンダリング
  if (activeTab === 'profile') return <Profile user={userInfo} events={events} appConfig={appConfig} onBack={() => setActiveTab('home')} setActiveTab={setActiveTab} />;
  if (activeTab === 'shop') return <Shop user={userInfo} appConfig={appConfig} onBack={() => setActiveTab('home')} />;
  if (activeTab === 'shrine') return <Shrine user={userInfo} appConfig={appConfig} onBack={() => setActiveTab('home')} />;
  if (activeTab === 'news') return <NewsDetail onBack={() => setActiveTab('home')} />;
  if (activeTab === 'gacha') return <Gacha user={userInfo} users={allUsers} appConfig={appConfig} onBack={() => setActiveTab('home')} />;
  if (activeTab === 'training') return <Training user={userInfo} appConfig={appConfig} onBack={() => setActiveTab('profile')} />;
  if (activeTab === 'deck') return <DeckBuilder user={userInfo} users={allUsers} onBack={() => setActiveTab('profile')} />;
  // ★追加: スポンサー（提携パートナー特典）
  //   isAdmin は「掲載期限が近い」バッジを運営だけに見せるためのもの。
  //   表示制御のみで、権限判定には使っていない（本来の権限は Firestore ルール側）。
  //   ★下部メニューを一緒に描画する。特典ページは行き止まりになりやすく、
  //     戻る導線が上部の「← 戻る」だけだと押しづらいため。
  //     activeTab が 'sponsors' なのでどのタブもハイライトされない（意図どおり）。
  //   ★運営への問い合わせ（管理室）。相手は運営固定なので一覧画面は不要。
  if (activeTab === 'contact') return (
      <>
          <ContactRoom user={userInfo} setActiveTab={setActiveTab} appConfig={appConfig} />
          <BottomNav activeTab={activeTab} setActiveTab={setActiveTab} />
      </>
  );

  if (activeTab === 'sponsors') return (
      <>
          <SponsorsPage
              userInfo={userInfo}
              setActiveTab={setActiveTab}
              isAdmin={userInfo?.role === 'staff' || userInfo?.role === 'admin'}
          />
          <BottomNav activeTab={activeTab} setActiveTab={setActiveTab} />
      </>
  );
  if (activeTab === 'sponsorApply') return (
      <>
          <SponsorApplyForm
              userInfo={userInfo}
              lineUser={lineUser}
              setActiveTab={setActiveTab}
          />
          <BottomNav activeTab={activeTab} setActiveTab={setActiveTab} />
      </>
  );
  if (activeTab === 'arena') return (
      <Arena 
          user={userInfo} 
          users={allUsers} 
          onBack={() => setActiveTab('home')} 
          onStartMatch={(rival, myStats, isPaid) => { 
              setMatchData({ rival, myStats, isPaid }); 
              setActiveTab('match');
          }}
      />
  );
  if (activeTab === 'match') {
      if (!matchData?.rival) {
          setActiveTab('arena');
          return null;
      }
      return (
          <Match 
              user={userInfo} 
              rival={matchData.rival} 
              myStats={matchData.myStats}             
              rivalStats={matchData.rival.teamStats || {}}  
              isPaid={matchData.isPaid}
              onBack={() => setActiveTab('arena')} 
          />
      );
  }

  return (
    <div className="app-container">
      <Toaster position="top-center" reverseOrder={false} />

      {(activeTab === 'home' || activeTab === 'news') && (
        <div style={{position:'relative', marginBottom:'20px'}}>
              {appConfig.topBannerUrl ? (
                  <div style={{position:'relative', width:'100%', height:'200px', overflow:'hidden', borderRadius:'0 0 20px 20px', boxShadow:'0 4px 15px rgba(0,0,0,0.2)'}}>
                      <img src={appConfig.topBannerUrl} alt="Banner" style={{width:'100%', height:'100%', objectFit:'cover'}} />
                  </div>
              ) : (
                  <div style={{background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`, borderRadius:'0 0 30px 30px', padding:'40px 20px 60px 20px', color:'white', textAlign:'center', boxShadow:'0 4px 15px rgba(0,0,0,0.2)'}}>
                     <h1 style={{ margin: 0, fontSize: '24px', letterSpacing:'1px', whiteSpace:'pre-wrap' }}>{appConfig.appTitle || 'Revengers FC'}</h1>
                  </div>
              )}
              
              {lineUser && (
                  <div onClick={() => setActiveTab('profile')} style={{position:'absolute', bottom:'-20px', left:'50%', transform:'translateX(-50%)', background:'white', padding:'8px 20px', borderRadius:'30px', boxShadow:'0 4px 10px rgba(0,0,0,0.1)', display:'flex', alignItems:'center', cursor:'pointer', zIndex:10}}>
                      {lineUser.pictureUrl && <img src={lineUser.pictureUrl} alt="icon" style={{width:'24px', height:'24px', borderRadius:'50%', marginRight:'8px'}} />}
                      <span style={{fontSize:'12px', fontWeight:'bold', color:'#333'}}>{lineUser.displayName}</span>
                  </div>
              )}
        </div>
      )}

      {activeTab === 'team' && (
          <TeamSection appConfig={appConfig} teamMembers={teamMembers} />
      )}

      <div className="container" style={{ padding: '0 15px', marginTop: activeTab === 'home' ? '40px' : '20px' }}>
        
        {activeTab === 'home' && (
             <HomeBody 
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                onSelectDateWrapper={(d) => { setSelectedDate(d); window.scrollTo({ top: 300, behavior: 'smooth' }); }}
                getTileContent={getTileContent}
                events={events}
                displayEvents={displayEvents}
                isLoading={isLoading}
                appConfig={appConfig}
                lineUser={lineUser}
                userInfo={userInfo}
                openChatId={openChatId}
                activeChatMessages={activeChatMessages}
                setOpenChatId={setOpenChatId}
                chatInput={chatInput}
                setChatInput={setChatInput}
                setActiveTab={setActiveTab}
                handlers={{
                    handleEventAction,
                    handleCheckIn,
                    handleCancelSelfCheckIn,
                    handlePayPayAction,
                    handlePointPayment,
                    handleCashPayment,
                    handleShareResult,
                    handlePostChat,
                    handleDeleteChat,
                    handleMemberClick: (userId) => setProfileModalUserId(userId)
                }}
             />
        )}

        {activeTab === 'ranking' && (
             <RankingSection 
                appConfig={appConfig}
                rankingMode={rankingMode}
                setRankingMode={setRankingMode}
                rankingData={rankingData}
             />
        )}

        {activeTab === 'guide' && <GuideSection appConfig={appConfig} />}

      </div>

      <AdBanner config={appConfig.adConfig} />

      {profileModalUserId && (
          <UserProfileModal 
              targetUserId={profileModalUserId}
              currentUserId={userInfo?.userId}
              onClose={() => setProfileModalUserId(null)}
              onFollow={(id, profile) => {
                  toast.success(`${profile.displayName}さんをフォローしました！\n（※裏側の機能は次のステップで実装します）`);
              }}
              onMessage={(id, profile) => {
                  toast.success('メッセージ画面へ移動します！\n（※裏側の機能は次のステップで実装します）');
              }}
              onBlock={(id, profile) => {
                  toast.success(`${profile.displayName}さんをブロックしました。`);
                  setProfileModalUserId(null); 
              }}
          />
      )}

      <BottomNav activeTab={activeTab} setActiveTab={setActiveTab} />
    </div>
  );
}

export default App;